<div class="cd-page">
  <app-counselor-nav></app-counselor-nav>

  <main class="cd-main" aria-label="Manage Roles">
    <div class="mr-header">
      <div>
        <h1 class="cd-title">Manage Roles</h1>
        <p class="cd-subtitle">Create, edit, and delete roles and the permissions they grant</p>
      </div>
      <button
        type="button"
        class="mu-btn mu-btn-cancel"
        (click)="back()"
        aria-label="Back to user management"
        data-testid="manage-roles-back-btn"
      >
        Back to Users
      </button>
    </div>

    @if (errorMessage) {
      <p class="mu-loading" role="alert" data-testid="manage-roles-error">{{ errorMessage }}</p>
    }

    <section class="mu-section" aria-label="Roles">
      <div class="mr-section-head">
        <h2 class="mu-section-title">Roles</h2>
        <button
          type="button"
          class="mu-btn mu-btn-save"
          (click)="startCreate()"
          aria-label="Create a new role"
          data-testid="new-role-btn"
          *hasPermission="perms.ROLES.CREATE"
        >
          New Role
        </button>
      </div>

      @if (loading) {
        <p class="mu-loading">Loading roles...</p>
      }

      @if (!loading && roles.length === 0) {
        <p class="mu-empty">No roles found.</p>
      }

      @if (!loading && roles.length > 0) {
        <div class="mr-role-list">
          @for (role of roles; track role.id) {
            <div class="mr-role-row" [attr.data-testid]="'role-row-' + role.id">
              <div class="mr-role-info">
                <div class="mr-role-name-line">
                  <span class="mr-role-name">{{ role.name }}</span>
                  <span
                    class="um-badge"
                    [class.um-badge-type]="role.is_custom"
                    [class.um-badge-system]="!role.is_custom"
                  >
                    {{ role.is_custom ? 'Custom' : 'System' }}
                  </span>
                </div>
                @if (role.description) {
                  <span class="mr-role-desc">{{ role.description }}</span>
                }
                <span class="mr-role-perms">{{ getPermissionCount(role) }} permission(s)</span>
              </div>

              <div class="mr-role-actions">
                @if (role.is_custom) {
                  @if (confirmingDeleteId === role.id) {
                    <span class="mr-confirm-label">Delete this role?</span>
                    <button
                      type="button"
                      class="mu-btn mu-btn-danger"
                      (click)="confirmDelete(role)"
                      [disabled]="deletingId === role.id"
                      [attr.aria-label]="'Confirm delete role ' + role.name"
                      [attr.data-testid]="'confirm-delete-btn-' + role.id"
                    >
                      {{ deletingId === role.id ? 'Deleting...' : 'Confirm' }}
                    </button>
                    <button
                      type="button"
                      class="mu-btn mu-btn-cancel"
                      (click)="cancelDelete()"
                      [disabled]="deletingId === role.id"
                      aria-label="Cancel delete"
                      [attr.data-testid]="'cancel-delete-btn-' + role.id"
                    >
                      Cancel
                    </button>
                  } @else {
                    <button
                      type="button"
                      class="mu-btn mu-btn-manage"
                      (click)="startEdit(role)"
                      [attr.aria-label]="'Edit role ' + role.name"
                      [attr.data-testid]="'edit-role-btn-' + role.id"
                      *hasPermission="[perms.ROLES.EDIT, perms.ROLES.MANAGE_PERMISSIONS]"
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      class="mu-btn mu-btn-danger"
                      (click)="requestDelete(role.id)"
                      [disabled]="role.can_assign === false"
                      [attr.title]="
                        role.can_assign === false
                          ? 'This role has permissions you do not hold'
                          : null
                      "
                      [attr.aria-label]="'Delete role ' + role.name"
                      [attr.data-testid]="'delete-role-btn-' + role.id"
                      *hasPermission="perms.ROLES.DELETE"
                    >
                      Delete
                    </button>
                  }
                } @else {
                  <span class="mr-locked">Managed by system</span>
                }
              </div>
            </div>
          }
        </div>
      }
    </section>

    @if (formMode !== 'closed') {
      <section class="mu-section" aria-label="Role form" data-testid="role-form">
        <h2 class="mu-section-title">
          {{ formMode === 'edit' ? 'Edit Role' : 'Create Role' }}
        </h2>

        @if (formError) {
          <p class="mr-form-error" role="alert" data-testid="role-form-error">{{ formError }}</p>
        }

        <div class="cr-field">
          <label class="cr-label" for="mr-name">Role Name</label>
          <input
            id="mr-name"
            type="text"
            class="cr-input"
            placeholder="e.g. Career Advisor"
            [(ngModel)]="roleName"
            aria-label="Role name"
            data-testid="role-name-input"
            required
          />
        </div>

        <div class="cr-field">
          <label class="cr-label" for="mr-description">
            Description <span class="cr-optional">(optional)</span>
          </label>
          <input
            id="mr-description"
            type="text"
            class="cr-input"
            placeholder="What is this role for?"
            [(ngModel)]="roleDescription"
            aria-label="Role description"
            data-testid="role-description-input"
          />
        </div>

        <div class="cr-field">
          <span class="cr-label">Permissions</span>
          <div class="mu-perm-filters">
            <input
              type="text"
              class="mu-perm-search"
              placeholder="Search permissions..."
              [(ngModel)]="permissionSearch"
              aria-label="Search permissions"
              data-testid="permission-search-input"
            />
            <select
              class="mu-perm-category"
              [(ngModel)]="permissionCategoryFilter"
              aria-label="Filter by category"
              data-testid="permission-category-filter"
            >
              <option value="">All Categories</option>
              @for (cat of permissionCategories; track cat) {
                <option [value]="cat">{{ cat }}</option>
              }
            </select>
          </div>

          <div class="mu-items-grid">
            @for (perm of filteredPermissions; track perm.id) {
              <label
                class="mu-item"
                [class.mu-item-active]="hasPermissionSelected(perm.id)"
                [class.mu-item-disabled]="isPermissionLocked(perm)"
                [attr.title]="isPermissionLocked(perm) ? 'You do not hold this permission' : null"
              >
                <input
                  type="checkbox"
                  [checked]="hasPermissionSelected(perm.id)"
                  [disabled]="isPermissionLocked(perm)"
                  (change)="togglePermission(perm.id)"
                  [attr.aria-label]="'Toggle permission ' + perm.name"
                  [attr.data-testid]="'permission-checkbox-' + perm.id"
                />
                <div class="mu-item-content">
                  <span class="mu-item-name">{{ perm.name }}</span>
                  <span class="mu-item-key">{{ perm.key }}</span>
                  @if (perm.description) {
                    <span class="mu-item-desc">{{ perm.description }}</span>
                  }
                </div>
              </label>
            }
          </div>

          @if (filteredPermissions.length === 0) {
            <p class="mu-empty">No permissions match your search.</p>
          }
        </div>

        <div class="mu-actions">
          <button
            class="mu-btn mu-btn-save"
            (click)="save()"
            [disabled]="!canSave"
            aria-label="Save role"
            data-testid="role-save-btn"
          >
            {{ saving ? 'Saving...' : formMode === 'edit' ? 'Save Changes' : 'Create Role' }}
          </button>
          <button
            class="mu-btn mu-btn-cancel"
            (click)="closeForm()"
            [disabled]="saving"
            aria-label="Cancel"
            data-testid="role-cancel-btn"
          >
            Cancel
          </button>
        </div>
      </section>
    }
  </main>
</div>
